import React, { useState, useEffect } from "react";

interface CancelOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  orderNumber?: string;
}

const CancelOrderModal: React.FC<CancelOrderModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  orderNumber = "",
}) => {
  const [reason, setReason] = useState("");

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (reason.trim()) {
      onConfirm(reason);
      setReason("");
      onClose();
    }
  };

  const handleClose = () => {
    setReason("");
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end md:items-center md:justify-center bg-black bg-opacity-60 p-0 md:p-4"
      onClick={handleOverlayClick}
    >
      {/* Modal Container */}
      <div
        className="w-full md:w-auto md:min-w-[400px] md:max-w-[500px] bg-white rounded-t-[24px] md:rounded-[16px] 
        animate-slide-up md:animate-none shadow-xl transform transition-all duration-300 ease-out"
      >
        {/* Header */}
        <div className="flex justify-between items-center p-5 md:p-6 border-b border-gray-200">
          <h2 className="text-[#050505] font-Urbanist text-[18px] md:text-[20px] font-semibold leading-[140%]">
            Cancel Order
          </h2>
          <button
            onClick={handleClose}
            className="text-[#FF8A00] font-Urbanist text-[14px] md:text-[16px] font-medium hover:underline"
          >
            Undo
          </button>
        </div>

        {/* Content */}
        <div className="p-5 md:p-6">
          <label className="block text-[#050505] font-Urbanist text-[14px] md:text-[16px] font-medium mb-3 leading-[140%]">
            Why are you cancelling the Order?
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter the reason for cancellation"
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg font-Urbanist text-[14px] 
            focus:outline-none focus:border-[#FF8A00] transition-colors resize-none placeholder:text-gray-400"
          />
        </div>

        {/* Footer */}
        <div className="p-5 md:p-6 pt-0">
          <button
            onClick={handleConfirm}
            disabled={!reason.trim()}
            className="w-full bg-[#FF3300] hover:bg-[#E62E00] disabled:bg-gray-300 disabled:cursor-not-allowed 
            text-white font-Urbanist text-[16px] md:text-[18px] font-semibold py-3 md:py-3.5 rounded-full 
            transition-colors leading-[140%]"
          >
            Cancel this Order
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelOrderModal;
